import React, { useEffect, useRef } from 'react';

import { GlobeIcon } from '../../components/icons';
import { type MorphGhost, morphTotalMs } from '../lib/morph';
import { Overlay, Ghost, Skin, Num, GlobeGlyph } from './MorphGhosts.styles';

interface MorphGhostsProps {
  /** Measured flight specs, one per stage box (see `measureGhosts`). */
  ghosts: MorphGhost[];
  /** Replay the same measured flight backwards (rail → boxes) when going back. */
  reverse?: boolean;
  /** Fired once the last box has landed — the caller then hands off to the real
   *  progress rail (or intro boxes) and unmounts this overlay in a single paint. */
  onDone: () => void;
}

/** Per-box CSS vars the keyframes read. Custom properties aren't in the
 *  `CSSProperties` type, so the literal is cast (the area's established pattern
 *  for inline `--*` vars, see LumioMobile / DevicePreview). */
const ghostStyle = (g: MorphGhost): React.CSSProperties =>
  ({
    left: `${g.startCx}px`,
    top: `${g.startCy}px`,
    '--dx': `${g.dx}px`,
    '--dy': `${g.dy}px`,
    '--from-w': `${g.fromW}px`,
    '--from-h': `${g.fromH}px`,
    '--mid-h': `${g.midH}px`,
    '--to-w': `${g.toW}px`,
    '--to-h': `${g.toH}px`,
    '--delay': `${g.delay}ms`,
  }) as React.CSSProperties;

/**
 * The flying overlay for the intro↔progress morph: four absolutely-positioned
 * ghost clones of the intro stage boxes, each animating (pure CSS) between its
 * measured resting rect and its measured destination slot in the top rail. The
 * hero (orange) box leads with a faint glow; the globe box keeps its glyph while
 * its plate dissolves. `reverse` replays the exact same flight backwards.
 */
export const MorphGhosts = ({ ghosts, reverse = false, onDone }: MorphGhostsProps): React.ReactElement => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      onDone();
    };
    // Complete off the ACTUAL animation finish, not a fixed timer: the step body
    // (world map etc.) mounting mid-morph can stall the CSS clock, and a timer
    // would then fire while the boxes are still in flight — the visible "snap" at
    // the handoff. getAnimations().finished tracks the real end; the (slightly
    // longer) timer is only a safety net if the API is unavailable / never settles.
    const el = overlayRef.current;
    const anims = el && typeof el.getAnimations === 'function' ? el.getAnimations({ subtree: true }) : [];
    if (anims.length > 0) {
      Promise.all(anims.map((a) => a.finished.then(() => undefined).catch(() => undefined))).then(done);
    }
    const id = window.setTimeout(done, morphTotalMs(ghosts) + 150);
    return () => {
      settled = true;
      window.clearTimeout(id);
    };
  }, [ghosts, onDone]);

  return (
    <Overlay ref={overlayRef} aria-hidden>
      {ghosts.map((g) => (
        <Ghost key={g.key} style={ghostStyle(g)}>
          <Skin $kind={g.kind} $reverse={reverse}>
            {g.numeral !== null ? (
              <Num $hero={g.kind === 'hero'} $reverse={reverse}>
                {g.numeral}
              </Num>
            ) : (
              <GlobeGlyph $reverse={reverse}>
                <GlobeIcon size={16} />
              </GlobeGlyph>
            )}
          </Skin>
        </Ghost>
      ))}
    </Overlay>
  );
};

export default MorphGhosts;
